/**
 * "A GESTURE JUST STARTED A RECOMPUTE" — one bit, per declaration, from the
 * mutation that knows it to the page that has to refetch when it ends.
 *
 * WHY THIS EXISTS. A confirm-class gesture (statement confirm, statement scope,
 * edition, scope proof, verify, a condition save with `confirm`) returns as soon as
 * its value is committed; the cone it fires is detached and can settle minutes
 * later. The review page refetches its interface-backed reads on that settle
 * (`useRefetchOnSettled`), but only a poll that is RUNNING sees the settle, and
 * the page's poll must not run permanently: every `/run` answer carries the whole
 * node matrix (~2.5 MB on a 10k-article declaration, every 1.2 s). So the gesture
 * arms it — and the gesture lives in a mutation hook several components below the
 * page, which is why this is a signal and not a prop.
 *
 * Module state, deliberately, like `useRunPoll`'s cursor: the mutation hooks are
 * called from many surfaces, none of them a common parent of the page. Fired in
 * each hook's own `onSuccess`, so every caller gets it without remembering to.
 */
import { useEffect } from "react"

const listeners = new Map<string, Set<() => void>>()

/** Tell whoever watches `declId` that a recompute was just requested. */
export function announceRecompute(declId: string): void {
  for (const listener of listeners.get(declId) ?? []) listener()
}

/** Call `onRecompute` whenever a gesture on `declId` starts a recompute. Pass a
 *  stable callback (`useRunPoll`'s `watch` is one): a new identity resubscribes. */
export function useOnRecompute(declId: string, onRecompute: () => void): void {
  useEffect(() => {
    if (!declId) return undefined
    let set = listeners.get(declId)
    if (!set) {
      set = new Set()
      listeners.set(declId, set)
    }
    set.add(onRecompute)
    const own = set
    return () => {
      own.delete(onRecompute)
      if (own.size === 0 && listeners.get(declId) === own)
        listeners.delete(declId)
    }
  }, [declId, onRecompute])
}
